// The moment a person acts on a task, wherever its form is (the task page,
// the modal, a timeline card): the button they pressed takes a check at once
// and the other choices step back; when the server accepts it, a check pops in
// the middle of the screen with what they did under it, and fades. Only a response that says the action
// took effect (X-Task-Action) stamps. The stamp hangs off <body>, so the task's
// frame re-rendering (by this response or by a broadcast) cannot cut it short.
// A refused submission puts everything back.

const TASK_ACTION = /\/tasks\/([^/?#]+)\/task_action/

export function attachTaskActionFeedback(doc = document) {
  const win = doc.defaultView || window
  if (win.__taskActionFeedbackAttached) return
  win.__taskActionFeedbackAttached = true

  doc.addEventListener("turbo:submit-start", (event) => {
    const form = event.target
    if (!isTaskAction(form)) return
    const submitter = event.detail?.formSubmission?.submitter
    form.dataset.acting = "true"
    if (submitter) submitter.classList.add("is-pressed")
  })

  doc.addEventListener("turbo:submit-end", (event) => {
    const form = event.target
    if (!isTaskAction(form)) return
    const submitter = event.detail?.formSubmission?.submitter
    delete form.dataset.acting
    submitter?.classList.remove("is-pressed")
    if (!event.detail?.success) return

    // A 2xx is not enough: the server says whether the action took effect
    // (PaperworkTasksController X-Task-Action). Only "done" and "note" stamp.
    const outcome = event.detail.fetchResponse?.response?.headers?.get("X-Task-Action")
    if (outcome !== "done" && outcome !== "note") return

    stamp(doc, {
      words: outcome === "note" ? "Note added" : stampWords(submitter),
      tone: outcome === "note" ? "done" : stampTone(submitter?.value)
    })
  })
}

function isTaskAction(form) {
  return form instanceof HTMLFormElement && TASK_ACTION.test(form.action || "")
}

// What the stamp says: the choice as done ("Mark done" stamps "Marked done",
// "Approve" stamps "Approved"), or "Answer sent" for a written answer. A label
// that already reads as an outcome ("Invoice requested") stays as it is.
const PAST = { mark: "Marked", approve: "Approved", reject: "Rejected", confirm: "Confirmed", accept: "Accepted",
  cancel: "Canceled", complete: "Completed", resolve: "Resolved", close: "Closed", skip: "Skipped", request: "Requested" }

function stampWords(submitter) {
  const label = (submitter?.textContent || "").replace(/\s+/g, " ").trim()
  if (!label || /^send( answer)?$/i.test(label) || /^submit$/i.test(label)) return "Answer sent"
  const [verb, ...rest] = label.split(" ")
  const past = PAST[verb.toLowerCase()]
  return past ? [past, ...rest].join(" ") : label
}

function stampTone(value = "") {
  // PaperworkTask.encode_action_identifier: "transition$$resolution".
  const action = String(value).split("$$")[0]
  if (action === "reject") return "bad"
  if (action === "cancel") return "off"
  return "done"
}

const MARKS = {
  done: '<path d="M5 12 10 17 19 7"/>',
  bad: '<path d="M7 7 17 17M17 7 7 17"/>',
  off: '<path d="M6 18 18 6"/>'
}

function stamp(doc, { words, tone }) {
  const node = doc.createElement("div")
  node.className = "task-stamp"
  node.dataset.tone = tone
  node.setAttribute("role", "status")
  node.setAttribute("aria-live", "polite")

  const mark = doc.createElement("span")
  mark.className = "task-stamp__mark"
  mark.setAttribute("aria-hidden", "true")
  mark.innerHTML = `<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="3" stroke-linecap="round" stroke-linejoin="round">${MARKS[tone] || MARKS.done}</svg>`
  // A few paper flecks fly out from behind the check.
  for (let i = 0; i < 8; i++) {
    const fleck = doc.createElement("i")
    fleck.className = "task-stamp__fleck"
    fleck.style.setProperty("--angle", `${i * 45 + 20}deg`)
    mark.append(fleck)
  }
  const said = doc.createElement("span")
  said.className = "task-stamp__words"
  said.textContent = words
  node.append(mark, said)

  const win = doc.defaultView || window
  doc.body.append(node)
  node.addEventListener("animationend", () => node.remove(), { once: true })
  // A stamp never outlives its moment, even where animations do not run.
  win.setTimeout(() => node.remove(), 1800)
}
